import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Card, List, Alert, Spin, Collapse } from 'antd';
import moment from 'moment';
import { useAIAnalysis } from '../shared/hooks';
import AdditionalQuestionnaires from './AdditionalQuestionnaires';
import { QuestionnaireResponse, QuestionnaireStructure, QuestionText } from '../shared/types';

const { Panel } = Collapse;

const NewAIQuestionnaireClient = ({ client }: { client: any }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [clientAge, setClientAge] = useState<number | null>(null);
  const [existingResponses, setExistingResponses] = useState<QuestionnaireResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [showExistingResponses, setShowExistingResponses] = useState(false);
  const [questionnaireStructure, setQuestionnaireStructure] = useState<QuestionnaireStructure | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);

  // Use AI Analysis hook
  const { additionalQuestionnaires, loading: loadingAI, error: aiError } = useAIAnalysis(client);

  console.log('Client data in NewAIQuestionnaireClient:', client);
  const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
  const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

  // Function to fetch questionnaire structure
  const fetchQuestionnaireStructure = useCallback(async (questionnaireId: string) => {
    console.log('[NewAIQuestionnaireClient] Fetching questionnaire structure for:', questionnaireId);
    setLoadingStructure(true);

    try {
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaires/${questionnaireId}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[NewAIQuestionnaireClient] Questionnaire structure fetched:', data);
      setQuestionnaireStructure(data);
    } catch (err: any) {
      console.error('[NewAIQuestionnaireClient] Failed to fetch questionnaire structure:', err);
    } finally {
      setLoadingStructure(false);
    }
  }, [API_URL, API_KEY]);

  // Function to fetch existing questionnaire responses
  const fetchExistingResponses = useCallback(async () => {
    if (!client?.id) {
      console.error('[NewAIQuestionnaireClient] Client ID is missing:', client);
      return;
    }

    console.log('[NewAIQuestionnaireClient] Checking for existing responses for client:', client.id);
    setLoadingResponses(true);
    setResponseError(null);

    try {
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaire-responses/user/${client.id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[NewAIQuestionnaireClient] Existing responses fetched:', data);
      setExistingResponses(data);
      
      // If there are existing responses, fetch the questionnaire structure for the first response
      if (data && data.length > 0 && data[0].questionnaireId) {
        await fetchQuestionnaireStructure(data[0].questionnaireId);
      }
      
      // If there are existing responses, show them by default
      if (data && data.length > 0) {
        setShowExistingResponses(true);
      }
    } catch (err: any) {
      console.error('[NewAIQuestionnaireClient] Failed to fetch existing responses:', err);
      const errorMessage = err.message || 'Failed to fetch existing responses';
      setResponseError(errorMessage);
    } finally {
      setLoadingResponses(false);
    }
  }, [client, fetchQuestionnaireStructure, API_URL, API_KEY]);

  useEffect(() => {
    if (client?.bio?.dateOfBirth) {
      const age = moment().diff(client.bio.dateOfBirth, 'years');
      setClientAge(age);
      const baseUrl = process.env.REACT_APP_QUESTIONNAIRE_UI;
      const sessionId = client.id;
      const url = age < 15
        ? `${baseUrl}/questionnaire/myhealth-intake-child?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`
        : `${baseUrl}/questionnaire/myhealth-intake?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`;
      setIframeUrl(url);
      console.log('Determined iframe URL:', url);
      
      // Check for existing responses when component loads
      fetchExistingResponses();
    }
  }, [client, fetchExistingResponses]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleClose = () => {
    setIsModalVisible(false);
  };

  const toggleExistingResponses = () => {
    setShowExistingResponses(!showExistingResponses);
  };

  // Helper functions for formatting
  const formatQuestionId = (questionId: string): string => {
    // Convert question IDs like 'gen-q3' to more readable format
    return questionId.replace(/-/g, ' ').toUpperCase();
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get question text from questionnaire structure
  const getQuestionText = (questionId: string, language: string = 'en'): string => {
    if (!questionnaireStructure) {
      return formatQuestionId(questionId); // Fallback to formatted ID
    }

    // Search through all sections and questions to find the matching question ID
    for (const section of questionnaireStructure.sections) {
      for (const question of section.questions) {
        if (question.id === questionId) {
          return question.text[language as keyof QuestionText] || question.text.en;
        }
      }
    }

    // If not found, return formatted question ID as fallback
    return formatQuestionId(questionId);
  };

  const renderQuestionnaireResponse = (response: QuestionnaireResponse) => {
    // Create a map of questionId to answerText for easy lookup
    const answerMap = new Map(response.questionResponses.map(qr => [qr.questionId, qr.answerText]));

    return (
      <Panel
        header={
          <div>
            <strong>{questionnaireStructure?.title[response.languageCode as keyof QuestionText] || questionnaireStructure?.title.en || response.questionnaireId}</strong>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Status: {response.status} | Started: {formatDate(response.startedAt)} | Language: {response.languageCode} | Questions Answered: {response.questionResponses?.length || 0}
            </div>
          </div>
        }
        key={response.id}
      >
        {questionnaireStructure ? (
          <div>
            {questionnaireStructure.sections.map(section => (
              <div key={section.id} style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: '16px', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
                  {section.title[response.languageCode as keyof QuestionText] || section.title.en}
                </h3>
                <List
                  dataSource={section.questions.sort((a, b) => a.sortOrder - b.sortOrder)}
                  renderItem={(question) => {
                    const answer = answerMap.get(question.id);
                    return (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <strong style={{ display: 'block', marginBottom: '4px' }}>
                            {question.text[response.languageCode as keyof QuestionText] || question.text.en}
                          </strong>
                          {answer !== undefined ? (
                            <div style={{ color: '#333', backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: '4px' }}>
                              {answer}
                            </div>
                          ) : (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                              No answer provided.
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          // Fallback to original implementation if structure is not yet loaded
          response.questionResponses && response.questionResponses.length > 0 ? (
            <List
              dataSource={response.questionResponses}
              renderItem={(questionResponse) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <strong>{getQuestionText(questionResponse.questionId, response.languageCode)}:</strong>
                    <div style={{ marginTop: '4px', color: '#333', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                      {questionResponse.answerText}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ color: '#666', fontStyle: 'italic' }}>
              No questions answered yet.
            </div>
          )
        )}
        {response.status === 'IN_PROGRESS' && (
          <Alert
            message="This questionnaire is in progress and can be continued."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Panel>
    );
  };

  // Check if there are any IN_PROGRESS responses that can be continued
  const hasInProgressResponse = existingResponses.some(response => response.status === 'IN_PROGRESS');
  
  // Check if there are any completed responses
  const hasCompletedResponse = existingResponses.some(response => response.status === 'COMPLETED');

  return (
    <div>
      {/* Loading indicator for checking existing responses */}
      {(loadingResponses || loadingStructure) && (
        <div style={{ marginBottom: 16 }}>
          <Spin size="small" /> 
          <span>
            {loadingResponses && loadingStructure 
              ? 'Loading responses and questionnaire details...'
              : loadingResponses 
                ? 'Checking for existing responses...' 
                : 'Loading questionnaire details...'
            }
          </span>
        </div>
      )}

      {/* Error alert for response fetching */}
      {responseError && (
        <Alert
          message="Error checking existing responses"
          description={responseError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Show existing responses if available */}
      {existingResponses.length > 0 && (
        <Card 
          title="Existing Questionnaire Responses" 
          style={{ marginBottom: 16 }}
          extra={
            <Button 
              type="text" 
              onClick={toggleExistingResponses}
              size="small"
            >
              {showExistingResponses ? 'Hide' : 'Show'} ({existingResponses.length})
            </Button>
          }
        >
          {showExistingResponses && (
            <>
              <Collapse accordion>
                {existingResponses.map((response) => renderQuestionnaireResponse(response))}
              </Collapse>
              {hasInProgressResponse && (
                <Alert
                  message="Continue Previous Questionnaire"
                  description="You have an incomplete questionnaire. Click 'Start/Continue Questionnaire' below to continue where you left off."
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
              {hasCompletedResponse && !hasInProgressResponse && (
                <Alert
                  message="Previous Responses Found"
                  description="You have completed this questionnaire before. You can start a new one if needed."
                  type="success"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </>
          )}
        </Card>
      )}

      {/* Main questionnaire link */}
      <Button type="link" onClick={showModal} style={{ padding: 0, height: 'auto' }}>
        {hasInProgressResponse 
          ? 'Continue Questionnaire' 
          : existingResponses.length > 0 
            ? 'Start New Questionnaire'
            : clientAge !== null && clientAge > 15 
              ? 'My Health Intake - Adult Version' 
              : 'My Health Intake - Child Version'
        }
      </Button>

      <Modal
        title="My Health Intake Questionnaire"
        open={isModalVisible}
        onCancel={handleClose}
        footer={null}
        width="100%"
        style={{ top: 0, padding: 0, height: '100vh' }}
        bodyStyle={{ padding: 0, height: '100%' }}
        destroyOnClose
      >
        {iframeUrl ? (
          <iframe
            src={iframeUrl}
            title="My Health Intake Questionnaire"
            width="100%"
            height="700px"
            style={{ border: 'none' }}
          />
        ) : (
          <p>Loading questionnaire...</p>
        )}
      </Modal>

      {/* AI Recommended Additional Questionnaires */}
      <AdditionalQuestionnaires
        questionnaires={additionalQuestionnaires}
        loading={loadingAI}
        error={aiError}
        client={client}
      />
    </div>
  );
};

export default NewAIQuestionnaireClient;
